import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { SyncLoader } from 'react-spinners';
import { StepCard } from '../components/StepCard';
import { setSelectedSlot, setSelectedOperatories, setViewMode, setSlotsFetchDurationOverride, setIsFirstSlotsFetch } from '../redux/slices/appointmentSlice';
import TimeSlotSelection from '../components/TimeSlotSelection';
import { formatDateToISO } from '../utils/helpers';
import { useIsSmallScreen } from '../hooks/useIsSmallScreen';
import { AvailableSlotsResponse, fetchAvailableSlots } from '../services/api';
import { SMALL_SCREEN_DAYS_TO_SHOW, LARGE_SCREEN_DAYS_TO_SHOW, MIN_SLOTS_THRESHOLD, EXTENDED_SLOTS_DAYS } from '../utils/constants';

export const AvailableSlots = () => {
  const dispatch = useDispatch();
  const patientType = useSelector((s: RootState) => s.appointment.patientType);
  const selectedTreatment = useSelector((s: RootState) => s.appointment.treatment);
  const selectedDurationMinutes = useSelector((s: RootState) => s.appointment.selectedDurationMinutes);
  const selectedSlot = useSelector((s: RootState) => s.appointment.selectedSlot);
  const { practice } = useSelector((s: RootState) => s.auth);
  const primetimeFilter = useSelector((s: RootState) => s.appointment.primetimeFilter);
  const { viewMode, slotsFetchDurationOverride, isFirstSlotsFetch } = useSelector((s: RootState) => s.appointment);

  const [loading, setLoading] = useState<boolean>(true);
  const [currentStart, setCurrentStart] = useState<Date>(new Date());
  const [slotsByDate, setSlotsByDate] = useState<Record<string, Record<string, string[]>>>({});

  const isSmallScreen = useIsSmallScreen();

  const daysToShow = useMemo(() => {
    if (slotsFetchDurationOverride) return slotsFetchDurationOverride;
    return isSmallScreen ? SMALL_SCREEN_DAYS_TO_SHOW : LARGE_SCREEN_DAYS_TO_SHOW;
  }, [isSmallScreen, slotsFetchDurationOverride]);

  const currentEnd = useMemo(() => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() + (daysToShow - 1));
    return d;
  }, [currentStart, daysToShow]);

  const goPrevWeek = () => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() - daysToShow);
    setCurrentStart(d);
  };

  const goNextWeek = () => {
    const d = new Date(currentStart);
    d.setDate(d.getDate() + daysToShow);
    setCurrentStart(d);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res: AvailableSlotsResponse = await fetchAvailableSlots({
          start_date: formatDateToISO(currentStart),
          end_date: formatDateToISO(currentEnd),
          new_patient: patientType === 'new',
          fixed_duration_slots: true,
          appointment_type_id: selectedTreatment?.id,
          ...(practice?.promote_primetime && { filters: primetimeFilter }),
        });
        const map: Record<string, Record<string, string[]>> = {};
        let totalSlotsCount = 0;

        for (const operatory of res || []) {
          const operatoryID = operatory?.id;
          const available = operatory?.attributes?.available_slots || {};
          for (const dateKey of Object.keys(available)) {
            const items = available[dateKey] || [];
            if (!map[dateKey]) map[dateKey] = {};
            for (const item of items) {
              const display = item.start_time;
              if (!map[dateKey][display]) {
                map[dateKey][display] = [];
                totalSlotsCount++;
              }
              map[dateKey][display].push(operatoryID);
            }
          }
        }

        if (isFirstSlotsFetch) {
          if (totalSlotsCount < MIN_SLOTS_THRESHOLD) {
            dispatch(setViewMode('list'));
            dispatch(setSlotsFetchDurationOverride(EXTENDED_SLOTS_DAYS));
            // Effect will trigger again because slotsFetchDurationOverride changed
          } else {
            dispatch(setViewMode('table'));
          }
          dispatch(setIsFirstSlotsFetch(false));
        }

        setSlotsByDate(map);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentStart, currentEnd, patientType, selectedTreatment?.id, selectedDurationMinutes, primetimeFilter, dispatch]);

  return (
    <StepCard>
      {loading ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <SyncLoader />
        </div>
      ) : (
        <TimeSlotSelection
          selectedSlot={selectedSlot}
          onSlotSelect={(slot) => dispatch(setSelectedSlot(slot))}
          onOperatoriesSelect={(ops) => dispatch(setSelectedOperatories(ops))}
          slotsByDate={slotsByDate}
          onPrevWeek={goPrevWeek}
          onNextWeek={goNextWeek}
          currentStart={currentStart}
          daysToShow={daysToShow}
          viewMode={viewMode || 'list'}
        />
      )}
    </StepCard>
  );
};
